// src/utils/purgeAllClientsAndKyc.js
import { collection, collectionGroup, getDocs, writeBatch } from "firebase/firestore";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { storage } from "../firebase";

async function deleteStorageObjects(objectPaths) {
  const paths = [...new Set((objectPaths || []).filter(Boolean))];
  if (!paths.length) return 0;

  const results = await Promise.allSettled(
    paths.map((path) => deleteObject(storageRef(storage, path)))
  );

  return results.filter((r) => r.status === "fulfilled").length;
}

async function deleteAllDocsInCollection(db, collectionName) {
  const snap = await getDocs(collection(db, collectionName));
  const docs = snap.docs;

  // Firestore batch limit = 500 operations, keep margin
  const BATCH_SIZE = 450;

  let deleted = 0;

  for (let i = 0; i < docs.length; i += BATCH_SIZE) {
    const slice = docs.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    slice.forEach((d) => batch.delete(d.ref));

    await batch.commit();
    deleted += slice.length;
  }

  return deleted;
}

async function deleteAllClientUploads(db) {
  const snap = await getDocs(collectionGroup(db, "client_uploads"));
  const objectPaths = snap.docs.map((d) => d.data()?.objectPath).filter(Boolean);

  await deleteStorageObjects(objectPaths);
  return deleteDocs(db, snap.docs);
}

async function deleteAllDocumentSubmissions(db) {
  const snap = await getDocs(collection(db, "documentSubmissions"));
  const objectPaths = snap.docs.flatMap((d) => {
    const files = Array.isArray(d.data()?.files) ? d.data().files : [];
    return files.map((f) => f?.objectPath).filter(Boolean);
  });

  await deleteStorageObjects(objectPaths);
  return deleteDocs(db, snap.docs);
}

async function deleteDocs(db, docsOrRefs) {
  if (!docsOrRefs.length) return 0;
  const BATCH_SIZE = 450;
  let deleted = 0;

  for (let i = 0; i < docsOrRefs.length; i += BATCH_SIZE) {
    const slice = docsOrRefs.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    slice.forEach((d) => batch.delete(d.ref ?? d));

    await batch.commit();
    deleted += slice.length;
  }

  return deleted;
}

export async function purgeAllClientsAndKyc(db) {
  const [
    deletedClientUploads,
    deletedClients,
    deletedForms,
    deletedSubmissions,
    deletedDrafts,
    deletedInvites,
    deletedDocSubmissions,
    deletedDocInvites,
    deletedEmailLogs,
  ] = await Promise.all([
    deleteAllClientUploads(db),
    deleteAllDocsInCollection(db, "clients"),
    deleteAllDocsInCollection(db, "kyc"),
    deleteAllDocsInCollection(db, "kyc_submissions"),
    deleteAllDocsInCollection(db, "kyc_client_drafts"),
    deleteAllDocsInCollection(db, "kyc_invites"),
    deleteAllDocumentSubmissions(db),
    deleteAllDocsInCollection(db, "doc_upload_invites"),
    deleteAllDocsInCollection(db, "email_logs"),
  ]);

  return {
    deletedClientUploads,
    deletedClients,
    deletedForms,
    deletedSubmissions,
    deletedDrafts,
    deletedInvites,
    deletedDocSubmissions,
    deletedDocInvites,
    deletedEmailLogs,
  };
}
